<template>
  <div class="subscription-management container mt-4">
    <h2 class="mb-4">
      <font-awesome-icon icon="fas fa-building" class="me-2" />
      Gestión de Suscripciones de Club
    </h2>

    <!-- Filtro -->
    <div class="card mb-4">
      <div class="card-body">
        <input v-model="filtroTexto" type="text" class="form-control"
          placeholder="Buscar por nombre de club o fecha..." />
      </div>
    </div>

    <!-- Alta rápida: crear/extender -->
    <div class="card mb-4">
      <div class="card-header">
        <h4>Extender Suscripción (+1 mes)</h4>
      </div>
      <div class="card-body row g-2 align-items-end">
        <div class="col-md-8">
          <label class="form-label">Seleccionar Club</label>
          <select v-model="nueva.club_id" class="form-select">
            <option disabled value="">Elija un club...</option>
            <option v-for="c in clubesFiltradosParaAlta" :key="c.id" :value="c.id">
              {{ c.nombre }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <button class="btn btn-primary w-100" @click="extenderClub">
            Extender un mes
          </button>
        </div>
      </div>
    </div>

    <!-- Tabla de clubes y suscripciones -->
    <div class="card">
      <div class="card-header">
        <h4>Clubs y suscripciones</h4>
      </div>
      <div class="card-body p-0">
        <table class="table table-striped mb-0">
          <thead>
            <tr>
              <th>Club</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="club in clubesActivosFiltrados" :key="club.id">
              <td>{{ club.nombre }}</td>
              <td>{{ formatDate(club.fecha_inicio_suscripcion) }}</td>
              <td>
                <input type="date" v-model="club.fecha_fin_model" class="form-control form-control-sm" />
              </td>
              <td>
                <span :class="{
                  'badge bg-success': isActive(club),
                  'badge bg-danger': !isActive(club)
                }">
                  {{ isActive(club) ? 'Sí' : 'No' }}
                </span>
              </td>
              <td>
                <button class="btn btn-sm btn-success me-1" @click="guardarClub(club)">
                  Guardar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from '@/axios'
import { parseISO, format } from 'date-fns'

const clubes = ref([])
const filtroTexto = ref('')
const nueva = ref({ club_id: '' })

// Carga inicial de clubes
const cargarClubes = async () => {
  try {
    const { data } = await axios.get('/api/clubes')
    clubes.value = data.map(c => ({
      ...c,
      fecha_fin_model: c.fecha_fin_suscripcion ? c.fecha_fin_suscripcion.slice(0, 10) : ''
    }))

  } catch (err) {
    console.error('Error al cargar clubes:', err)
  }
}

const hoy = new Date().toISOString().slice(0, 10)

const clubesActivosFiltrados = computed(() => {
  const texto = filtroTexto.value.toLowerCase()
  return clubes.value.filter(c => {
    const inicio = c.fecha_inicio_suscripcion?.slice(0, 10)
    const fin = c.fecha_fin_model
    const activo = inicio && fin && inicio <= hoy && fin >= hoy
    return activo && (
      c.nombre.toLowerCase().includes(texto) ||
      fin.includes(texto)
    )
  })
})

const clubesFiltradosParaAlta = computed(() => {
  const texto = filtroTexto.value.toLowerCase()
  return clubes.value.filter(c =>
    c.nombre.toLowerCase().includes(texto)
  )
})


// Extender suscripción un mes
const extenderClub = async () => {
  if (!nueva.value.club_id) {
    return alert('Seleccione un club')
  }
  try {
    await axios.post(
      `/api/clubes_extender_suscripcion/${nueva.value.club_id}`,
      {},
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    )
    alert('Suscripción extendida 1 mes')
    await cargarClubes()
    nueva.value.club_id = ''
  } catch (err) {
    console.error(err)
    alert(err.response?.data?.error || 'Error al extender suscripción')
  }
}

// Guardar fecha_fin manualmente
const guardarClub = async (club) => {
  try {
    await axios.put(
      `/api/clubes_actualizar_suscripcion/${club.id}`,
      { fecha_fin: club.fecha_fin_model },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    )
    alert('Fecha de fin actualizada')
    await cargarClubes()
  } catch (err) {
    console.error(err)
    alert(err.response?.data?.error || 'Error al actualizar fecha')
  }
}

// Utilitarios
const formatDate = d => {
  if (!d) return '-'
  return format(parseISO(d.slice(0, 10)), 'dd/MM/yyyy')
}

const isActive = club => {
  const now = new Date().toISOString().slice(0, 10)
  return club.fecha_inicio_suscripcion?.slice(0, 10) <= now &&
    club.fecha_fin_model >= now
}

// Lifecycle
onMounted(() => {
  cargarClubes()
})
</script>

<style scoped>
.subscription-management {
  background-color: #f7f8fa;
  padding: 20px;
}

.table {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.table thead {
  display: none;
}

.table tbody {
  display: flex;
  flex-direction: column;
}

.table tr {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
}

.table td {
  display: flex;
  justify-content: space-between;
  padding: 10px;
}

@media (min-width: 768px) {
  .table thead {
    display: table-header-group;
  }

  .table tbody {
    display: table-row-group;
  }

  .table tr {
    display: table-row;
  }

  .table td {
    display: table-cell;
    padding: 8px;
  }
}

@media (max-width: 768px) {
  .table td:last-child {
    display: flex;
    justify-content: center;
    padding: 10px;
  }
}
</style>
